import React, { useEffect, useState } from 'react';
import styles from './user-management-dashboard.module.scss';
import { arrow_calendar } from './../../../../assets/img';
import Chart from 'react-apexcharts';
import DropdownWithApply from '../../../../components/DropdownWithApply/DropdownWithApply';
import DatePickerUserManagement from '../../../../components/DatePickerUserManagement/DatePickerUserManagement';
import CardListCustom from '../../../../components/CardListCustom/CardListCustom';
import { max } from 'lodash';
import { Spin } from 'antd';
import { adminApi } from '../../../../services/admin';
import { useSelector, useDispatch } from 'react-redux';
import { updateFilterUserDashboadText } from '../UserAccessManagementSlice';
import { adjustDate } from '../../../../utils/helper';

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const yearViewInitial = Math.floor(currentYear / 12) * 12;

const appliedDataInitial = {
  mode: 'month',
  monthOption: {
    selectedHeader: currentYear,
    selectedValue: [monthNames[currentMonth - 1]],
  },
  quarterOption: {
    selectedHeader: currentYear,
    selectedValue: [],
  },
  yearOption: {
    selectedHeader: yearViewInitial,
    selectedValue: [],
  },
};

const UserManagementDashboard = () => {
  const roleActive = useSelector((state) => state.user.roleActive);
  const dispatch = useDispatch();
  const [userStatistic, setUserStatistic] = useState({});
  const [chartData, setCharData] = useState({
    moduleName: [],
    moduleValue: [],
  });
  const [loading, setLoading] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({
    roles: [],
    businessAccess: [],
  });

  const [filter, setFilter] = useState({
    roles: [],
    business_access: [],
    date: {
      type: 'month',
      month: currentMonth,
      year: currentYear,
      quarter: null,
    },
  });

  const formatChartData = (chartData) => {
    const result = {
      moduleName: [],
      moduleValue: [],
    };

    if (Array.isArray(chartData) && chartData.length > 0) {
      chartData.forEach((item) => {
        result.moduleName.push(item.module_name);
        result.moduleValue.push(item.module_value);
      });
    }

    return result;
  };

  const handleApplyFilter = (selection, key) => {
    if (key === 'roles' || key === 'business_access') {
      setFilter((pre) => ({
        ...pre,
        [key]: [...selection],
      }));
      return;
    }

    const dateFilter = {
      type: selection.mode,
    };

    if (selection.mode === 'month') {
      dateFilter.month = selection.monthOption.selectedValue.map((m) => monthNames.indexOf(m) + 1)[0];
      dateFilter.year = selection.monthOption.selectedHeader;
      dateFilter.quarter = null;
    }

    if (selection.mode === 'quarter') {
      dateFilter.month = null;
      dateFilter.year = selection.quarterOption.selectedHeader;
      dateFilter.quarter = Math.floor(monthNames.indexOf(selection.quarterOption.selectedValue[0]) / 3) + 1;
    }

    if (selection.mode === 'year') {
      dateFilter.month = null;
      dateFilter.quarter = null;
      dateFilter.year = selection.yearOption.selectedValue[0];
    }

    setFilter((pre) => ({
      ...pre,
      date: dateFilter,
    }));
  };

  useEffect(() => {
    const getStatisticUserManagementDashboard = async () => {
      setLoading(true);
      const payload = {
        ...filter,
        date: {
          ...filter.date,
          ...adjustDate(filter.date),
        },
      };

      try {
        const response = await adminApi.getStatisticUserManagementDashboard(roleActive.roleId, payload);
        if (response?.status === 200 && response?.data?.result) {
          setUserStatistic(response.data.result.statistics || {});
          setCharData(formatChartData(response.data.result.chart_data || []));
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    dispatch(updateFilterUserDashboadText({ ...filter }));
    getStatisticUserManagementDashboard();
  }, [roleActive.roleId, filter]);

  useEffect(() => {
    const getFilterStatisticUserManagementDashboard = async () => {
      try {
        const response = await adminApi.getFilterStatisticUserManagementDashboard(roleActive.roleId);
        if (response?.status === 200 && response?.data?.result) {
          setDropdownOptions(response.data.result);
        }
      } catch (error) {
        console.log(error);
      }
    };

    getFilterStatisticUserManagementDashboard();
  }, [roleActive.roleId]);

  function computeYMax(dataMax, step = 50, padding = 1.1, fallback = 10) {
    if (dataMax === 0) return fallback;
    return Math.ceil((dataMax * padding) / step) * step;
  }

  return (
    <Spin spinning={loading}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>User Access Management Dashboard</div>
          <div className={styles.action}>
            <DropdownWithApply
              options={dropdownOptions.roles}
              onApply={(selection) => {
                handleApplyFilter(selection, 'roles');
              }}
              height={44}
              suffixIcon={<img src={arrow_calendar} alt="dropdown" />}
              placeholder="Role access"
            />
            <DropdownWithApply
              options={dropdownOptions.businessAccess}
              onApply={(selection) => {
                handleApplyFilter(selection, 'business_access');
              }}
              height={44}
              suffixIcon={<img src={arrow_calendar} alt="dropdown" />}
              placeholder="Business"
            />
            <DatePickerUserManagement
              onApply={(selection) => {
                handleApplyFilter(selection, 'date');
              }}
              appliedDataDefault={appliedDataInitial}
              isDisableApply={true}
            />
          </div>
        </div>

        <div className={styles.disclaimer}>
          Disclaimer: The data displayed in this dashboard reflects values as of the previous day and may not include real-time updates.
        </div>

        <div>
          <CardListCustom userStatistic={userStatistic} />
        </div>

        <div className={styles.chart}>
          <Chart
            type="bar"
            width="100%"
            height={700}
            series={[
              {
                name: '',
                data: chartData.moduleValue,
              },
            ]}
            options={{
              chart: {
                toolbar: {
                  show: false,
                },
                zoom: {
                  enabled: false,
                },
              },

              tooltip: {
                enabled: false,
              },

              title: {
                text: 'User Utilization by Module',
                style: { fontSize: 18, fontWeight: 700, color: '#3F3C4C', fontFamily: 'Inter' },
                offsetY: -5,
              },
              colors: ['#7AC8C5'],

              xaxis: {
                tickPlacement: 'on',
                labels: {
                  style: {
                    colors: '#4F5F6E',
                    fontSize: '12px',
                    fontWeight: 400,
                    fontFamily: 'Inter',
                  },
                },
                categories: chartData.moduleName,
              },

              yaxis: {
                min: 0,
                max: computeYMax(max(chartData.moduleValue) || 0),
                tickAmount: 5,
                labels: {
                  style: {
                    colors: '#4F5F6E',
                    fontSize: '12px',
                    fontWeight: 400,
                    fontFamily: 'Inter',
                  },
                },
              },

              plotOptions: {
                bar: {
                  horizontal: false,
                  columnWidth: '20%',
                  dataLabels: {
                    position: 'top',
                  },
                },
              },

              dataLabels: {
                enabled: true,
                formatter: function (val) {
                  return val;
                },
                offsetY: -20,
                style: {
                  fontSize: '14px',
                  colors: ['#394655'],
                  fontWeight: 500,
                  fontFamily: 'Inter',
                },
              },
              grid: {
                show: true,
                borderColor: '#AABCD0',
                strokeDashArray: 4,
                xaxis: {
                  lines: {
                    show: false,
                  },
                },
                yaxis: {
                  lines: {
                    show: true,
                  },
                },
              },
            }}
          ></Chart>
        </div>
      </div>
    </Spin>
  );
};

export default UserManagementDashboard;
